import { useState, useRef, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { Release, ReleaseVideo } from '../../types/release'
import { refreshReleaseStats } from '../../api/releases'
import { formatCurrency, cn } from '../../utils'
import { usePlayer } from '../../context/PlayerContext'
import { useToast } from '../../context/ToastContext'
import { useDiscogsConnection } from '../../context/DiscogsContext'
import { useDiscogsMembership } from '../../hooks/useDiscogsLists'
import { useDiscogsWriteGate } from '../DiscogsWriteGate'
import { formatReleaseLabel } from '../../utils/release'
import axiosClient from '../../api/client'
import { useDraggable } from '@dnd-kit/core'
import { CSS } from '@dnd-kit/utilities'
import { ArtistLinks } from '../common/ArtistLinks'
import { SellerListingInfo } from '../../api/sellers'
import { AddReleaseToFolderModal } from '../Folders/AddReleaseToFolderModal'
import { MAP_ENABLED } from '../../config/features'
import styles from './Releases.module.css'

interface ReleaseGridProps {
  releases: Release[]
  draggable?: boolean
  onRemove?: (releaseId: number) => void
  showRemoveButton?: boolean
  listingMap?: Map<number, SellerListingInfo>
}

export function ReleaseGrid({
  releases,
  draggable = false,
  onRemove,
  showRemoveButton = false,
  listingMap,
}: ReleaseGridProps) {
  return (
    <div className={styles.releaseGrid}>
      {releases.map(release => (
        <ReleaseGridCard
          key={`release-${release.id}`}
          release={release}
          draggable={draggable}
          onRemove={onRemove}
          showRemoveButton={showRemoveButton}
          listingInfo={listingMap?.get(release.id) ?? null}
        />
      ))}
    </div>
  )
}

interface ReleaseGridCardProps {
  release: Release
  draggable?: boolean
  onRemove?: (releaseId: number) => void
  showRemoveButton?: boolean
  listingInfo?: SellerListingInfo | null
}

function abbreviateCondition(condition: string): string {
  const abbreviation = condition.match(/\(([^)]+)\)/)?.[1]
  return abbreviation ?? condition
}

function getHistoricalMedianDelta(listingInfo: SellerListingInfo | null | undefined, median: number | null): number | null {
  // Historical sale medians are stored in EUR. Avoid presenting a misleading
  // percentage for listings in another currency until they are converted.
  if (listingInfo?.price == null || listingInfo.currency !== 'EUR' || median == null || median <= 0) return null
  return Math.round(((listingInfo.price - median) / median) * 100)
}

export function ReleaseGridCard({
  release,
  draggable = false,
  onRemove,
  showRemoveButton = false,
  listingInfo,
}: ReleaseGridCardProps) {
  const { hasVideos, playRelease, currentTrack, isPlaying, progress, duration, seekTo } = usePlayer()
  const { showToast } = useToast()
  const { isAuthenticated } = useDiscogsConnection()
  const { isInWantlist, addToWantlist, removeFromWantlist, isInCollection, addToCollection, removeFromCollection } = useDiscogsMembership()
  const { gate: discogsGate } = useDiscogsWriteGate()
  const releaseLabel = formatReleaseLabel(release)
  const [isTracked, setIsTracked] = useState(release.is_tracked === 1)
  const [fetchedVideos, setFetchedVideos] = useState<ReleaseVideo[] | null>(null)
  const [optimisticTrack, setOptimisticTrack] = useState<{ index: number; progressPct: number } | null>(null)
  const [folderPickerRelease, setFolderPickerRelease] = useState<Release | null>(null)
  const [overlayOpen, setOverlayOpen] = useState(false)
  const fetchAttemptedRef = useRef(false)
  const touchStartRef = useRef<{ x: number; y: number } | null>(null)

  // Merge fetched videos so player functions see them
  const effectiveRelease: Release = fetchedVideos ? { ...release, videos: fetchedVideos } : release

  const isThisReleasePlaying = isPlaying && currentTrack?.releaseId === release.id
  const playingVideoIndex = isThisReleasePlaying && effectiveRelease.videos
    ? effectiveRelease.videos.findIndex(v => v.title === currentTrack?.title)
    : -1

  const activeTrackRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (activeTrackRef.current) {
      activeTrackRef.current.scrollIntoView({ block: 'nearest', behavior: 'smooth' })
    }
  }, [playingVideoIndex])

  useEffect(() => {
    if (!optimisticTrack) return
    if (playingVideoIndex === optimisticTrack.index && progress > 0) {
      setOptimisticTrack(null)
    }
  }, [optimisticTrack, playingVideoIndex, progress])

  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id: `release-${release.id}`,
    data: { release },
    disabled: !draggable,
  })

  const style = transform ? {
    transform: CSS.Translate.toString(transform),
    opacity: isDragging ? 0.5 : 1,
  } : undefined

  const handleMouseEnter = () => {
    if (!hasVideos(release) && !fetchAttemptedRef.current) {
      fetchAttemptedRef.current = true
      refreshReleaseStats(release.id)
        .then(result => {
          if (result.success && result.videos?.length) {
            setFetchedVideos(result.videos as ReleaseVideo[])
          }
        })
        .catch(() => {})
    }
  }

  const handleImageTouchStart = (e: React.TouchEvent) => {
    touchStartRef.current = { x: e.touches[0].clientX, y: e.touches[0].clientY }
  }

  const handleImageTouchEnd = (e: React.TouchEvent) => {
    if (!touchStartRef.current) return
    const dx = Math.abs(e.changedTouches[0].clientX - touchStartRef.current.x)
    const dy = Math.abs(e.changedTouches[0].clientY - touchStartRef.current.y)
    touchStartRef.current = null
    if (dx > 10 || dy > 10) return
    if (!hasVideos(release) && !fetchAttemptedRef.current) {
      fetchAttemptedRef.current = true
      refreshReleaseStats(release.id)
        .then(result => {
          if (result.success && result.videos?.length) {
            setFetchedVideos(result.videos as ReleaseVideo[])
          }
        })
        .catch(() => {})
    }
    setOverlayOpen(prev => !prev)
  }

  const handleAddToWantlist = async (e: React.MouseEvent) => {
    e.stopPropagation()
    if (!isAuthenticated) {
      showToast('Please connect your Discogs account in Settings first', 'warning')
      return
    }

    const inWantlist = isInWantlist(release.id)
    const action = inWantlist ? 'remove-wantlist' : 'add-wantlist'
    const ok = await discogsGate(action, { releaseLabel })
    if (!ok) return

    try {
      if (inWantlist) {
        await axiosClient.delete(`/discogs/wantlist/${release.id}`)
        removeFromWantlist(release.id)
        showToast('Removed from Discogs wantlist', 'success')
      } else {
        await axiosClient.post('/discogs/wantlist', { releaseId: release.id })
        addToWantlist(release.id)
        showToast('Added to Discogs wantlist', 'success')
      }
    } catch (error: any) {
      showToast(error?.response?.data?.message || 'Failed to update wantlist', 'error')
    }
  }

  const handleToggleCollection = async (e: React.MouseEvent) => {
    e.stopPropagation()
    if (!isAuthenticated) {
      showToast('Please connect your Discogs account in Settings first', 'warning')
      return
    }

    const inCollection = isInCollection(release.id)
    const action = inCollection ? 'remove-collection' : 'add-collection'
    const ok = await discogsGate(action, { releaseLabel })
    if (!ok) return

    try {
      if (inCollection) {
        await axiosClient.delete(`/discogs/collection/${release.id}`)
        removeFromCollection(release.id)
        showToast('Removed from Discogs collection', 'success')
      } else {
        await axiosClient.post('/discogs/collection', { releaseId: release.id })
        addToCollection(release.id)
        showToast('Added to Discogs collection', 'success')
      }
    } catch (error: any) {
      showToast(error?.response?.data?.message || 'Failed to update collection', 'error')
    }
  }

  const handleToggleTrack = async (e: React.MouseEvent) => {
    e.stopPropagation()
    try {
      if (isTracked) {
        await axiosClient.delete(`/tracking/${release.id}`)
        setIsTracked(false)
        showToast('Removed from tracked releases', 'success')
      } else {
        await axiosClient.post(`/tracking/${release.id}`)
        setIsTracked(true)
        showToast('Added to tracked releases', 'success')
      }
    } catch (error) {
      showToast(error instanceof Error ? error.message : 'Failed to update tracking', 'error')
    }
  }


  const getThumbnail = (): string | null => {
    if (release.images && release.images.length > 0) {
      return release.images[0].uri || release.images[0].uri150
    }
    return null
  }

  const thumbnail = getThumbnail()
  const hasVideosResult = hasVideos(effectiveRelease)
  const historicalMedianDelta = getHistoricalMedianDelta(listingInfo, release.median)

  const handleRemove = (e: React.MouseEvent) => {
    e.stopPropagation()
    if (onRemove) {
      onRemove(release.id)
    }
  }

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...(draggable ? { ...attributes, ...listeners } : {})}
      className={cn(styles.gridCard, draggable && styles.draggable)}
      onMouseEnter={handleMouseEnter}
    >
      <div
        className={styles.gridCardImage}
        onTouchStart={handleImageTouchStart}
        onTouchEnd={handleImageTouchEnd}
      >
        {thumbnail ? (
          <img src={thumbnail} alt={release.title} />
        ) : (
          <div className={styles.gridCardNoImage}>
            <i className="bi bi-disc"></i>
          </div>
        )}

        <div className={cn(styles.gridHoverOverlay, (isThisReleasePlaying || overlayOpen) && styles.gridHoverOverlayOpen)}>
          <div className={styles.gridOverlayHeader}>
            <div className={styles.gridOverlayActions}>
              <button
                className={cn(styles.gridOverlayBtn, isTracked && styles.active)}
                onClick={handleToggleTrack}
                title={isTracked ? 'Untrack release' : 'Track release'}
              >
                <i className={`bi ${isTracked ? 'bi-bookmark-fill' : 'bi-bookmark'}`}></i>
              </button>
              <button
                className={cn(styles.gridOverlayBtn, isInWantlist(release.id) && styles.active)}
                onClick={handleAddToWantlist}
                disabled={!isAuthenticated}
                title={isAuthenticated ? (isInWantlist(release.id) ? 'Remove from wantlist' : 'Add to wantlist') : 'Login to use wantlist'}
              >
                <i className={`bi ${isInWantlist(release.id) ? 'bi-heart-fill' : 'bi-heart'}`}></i>
              </button>
              <button
                className={cn(styles.gridOverlayBtn, isInCollection(release.id) && styles.active)}
                onClick={handleToggleCollection}
                disabled={!isAuthenticated}
                title={isAuthenticated ? (isInCollection(release.id) ? 'Remove from collection' : 'Add to collection') : 'Login to use collection'}
              >
                <i className={`bi ${isInCollection(release.id) ? 'bi-collection-fill' : 'bi-collection'}`}></i>
              </button>
              {MAP_ENABLED && <Link
                to={`/map?releaseId=${release.id}`}
                className={cn(styles.gridOverlayBtn, styles.mobileHiddenGridAction)}
                onClick={e => e.stopPropagation()}
                title="Show on map"
              >
                <i className="bi bi-map"></i>
              </Link>}
              <button
                className={cn(styles.gridOverlayBtn, styles.mobileOnlyGridAction)}
                onClick={e => {
                  e.stopPropagation()
                  setFolderPickerRelease(release)
                }}
                title="Add to folder"
              >
                <i className="bi bi-folder-plus"></i>
              </button>
              {showRemoveButton && onRemove && (
                <button
                  className={styles.gridOverlayBtn}
                  onClick={handleRemove}
                  title="Remove from folder"
                >
                  <i className="bi bi-trash"></i>
                </button>
              )}
            </div>
            <a
              href={`https://www.discogs.com/release/${release.id}`}
              target="_blank"
              rel="noopener noreferrer"
              className={cn(styles.gridOverlayBtn, styles.mobileHiddenGridAction)}
              title="Open on Discogs"
              onClick={e => e.stopPropagation()}
            >
              <i className="bi bi-box-arrow-up-right"></i>
            </a>
          </div>

          <div className={styles.gridTrackList}>
            {hasVideosResult && effectiveRelease.videos && effectiveRelease.videos.length > 0 ? (
              effectiveRelease.videos.map((video, i) => {
                const isThisTrackPlaying = i === playingVideoIndex
                const optimisticProgressPct = optimisticTrack?.index === i ? optimisticTrack.progressPct : undefined
                const isThisTrackActive = isThisTrackPlaying || optimisticProgressPct !== undefined
                return (
                  <GridTrackItem
                    key={i}
                    title={video.title}
                    isPlaying={isThisTrackActive}
                    progress={isThisTrackPlaying ? progress : 0}
                    duration={isThisTrackPlaying ? duration : 0}
                    fallbackProgressPct={optimisticProgressPct}
                    onPlayAtRatio={(ratio) => {
                      setOptimisticTrack({ index: i, progressPct: ratio * 100 })
                      const count = playRelease(effectiveRelease, i, ratio)
                      if (count === 0) {
                        setOptimisticTrack(null)
                        showToast('Could not play this track', 'warning')
                      }
                    }}
                    onSeek={seekTo}
                    rowRef={isThisTrackActive ? activeTrackRef : undefined}
                  />
                )
              })
            ) : (
              <div className={styles.gridNoTracks}>
                <i className="bi bi-disc"></i>
                <span>No playable tracks</span>
              </div>
            )}
          </div>
        </div>

      </div>

      <div className={styles.gridCardContent}>
        <div className={styles.gridCardTitleRow}>
          <Link to={`/releases/${release.id}`} className={styles.gridCardTitle}>
            {release.title}
          </Link>
          {listingInfo?.price != null ? (
            (() => {
              const symbols: Record<string, string> = { EUR: '€', USD: '$', GBP: '£' }
              const sym = symbols[listingInfo.currency ?? ''] ?? (listingInfo.currency ? `${listingInfo.currency} ` : '')
              return <span className={styles.gridPriceBadge}>{sym}{listingInfo.price!.toFixed(2)}</span>
            })()
          ) : release.median != null ? (
            <span className={styles.gridPriceBadge}>{formatCurrency(release.median)}</span>
          ) : null}
        </div>
        <div className={styles.gridCardArtist}>
          <ArtistLinks artists={release.artists} />
          {release.year && <span className={styles.gridCardYear}>{release.year}</span>}
        </div>
        {listingInfo && (listingInfo.condition || historicalMedianDelta !== null) && (
          <div className={styles.gridListingMeta}>
            {listingInfo.condition && (
              <span
                className={styles.gridListingCondition}
                title={`Media condition: ${listingInfo.condition}`}
              >
                Media {abbreviateCondition(listingInfo.condition)}
              </span>
            )}
            {historicalMedianDelta !== null && (
              <span
                className={cn(
                  styles.gridMedianDelta,
                  historicalMedianDelta < 0 && styles.gridMedianDeltaBelow,
                  historicalMedianDelta > 0 && styles.gridMedianDeltaAbove,
                )}
                title={`Compared with the €${release.median!.toFixed(2)} historical sold median`}
              >
                {historicalMedianDelta === 0
                  ? 'At median'
                  : `${Math.abs(historicalMedianDelta)}% ${historicalMedianDelta < 0 ? 'below' : 'above'} median`}
              </span>
            )}
          </div>
        )}
      </div>

      {folderPickerRelease && (
        <AddReleaseToFolderModal release={folderPickerRelease} onClose={() => setFolderPickerRelease(null)} />
      )}
    </div>
  )
}

interface GridTrackItemProps {
  title: string
  isPlaying: boolean
  progress: number
  duration: number
  fallbackProgressPct?: number
  onPlayAtRatio: (ratio: number) => void
  onSeek: (seconds: number) => void
  rowRef?: React.RefObject<HTMLDivElement>
}

export function GridTrackItem({ title, isPlaying, progress, duration, fallbackProgressPct, onPlayAtRatio, onSeek, rowRef }: GridTrackItemProps) {
  const localRef = useRef<HTMLDivElement>(null)
  const ref = rowRef ?? localRef
  const [scrubPct, setScrubPct] = useState<number | null>(null)
  const draggingRef = useRef(false)

  const playedPct = duration > 0 ? Math.max(0, Math.min(100, (progress / duration) * 100)) : 0
  const progressPct = progress > 0 ? playedPct : fallbackProgressPct ?? playedPct
  const fillPct = scrubPct ?? (isPlaying ? progressPct : 0)

  const ratioFromEvent = (clientX: number): number => {
    const el = ref.current
    if (!el) return 0
    const rect = el.getBoundingClientRect()
    return Math.max(0, Math.min(1, (clientX - rect.left) / rect.width))
  }

  const handlePointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    if (e.button !== 0) return
    draggingRef.current = true
    if (isPlaying) {
      e.preventDefault()
      ref.current?.setPointerCapture(e.pointerId)
      setScrubPct(ratioFromEvent(e.clientX) * 100)
    }
  }

  const handlePointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!draggingRef.current || !isPlaying) return
    setScrubPct(ratioFromEvent(e.clientX) * 100)
  }

  const handlePointerUp = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!draggingRef.current) return
    draggingRef.current = false
    const ratio = ratioFromEvent(e.clientX)
    if (isPlaying) {
      try { ref.current?.releasePointerCapture(e.pointerId) } catch {}
      if (duration > 0) onSeek(ratio * duration)
      setScrubPct(null)
    } else {
      onPlayAtRatio(ratio)
    }
  }

  const handlePointerCancel = () => {
    draggingRef.current = false
    setScrubPct(null)
  }

  return (
    <div
      ref={ref}
      role="button"
      tabIndex={0}
      className={cn(styles.gridTrackItem, isPlaying && styles.gridTrackItemPlaying, isPlaying && styles.gridTrackItemScrubbable)}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerUp}
      onPointerCancel={handlePointerCancel}
    >
      {isPlaying && (
        <div
          className={styles.gridTrackProgress}
          style={{ width: `${fillPct}%` }}
        />
      )}
      <i className={cn(`bi ${isPlaying ? 'bi-pause-fill' : 'bi-play-fill'}`, styles.gridTrackIcon)}></i>
      <span className={styles.gridTrackLabel}>{title}</span>
    </div>
  )
}
